import type { UseFormReturn } from "react-hook-form"

import { AS400_FIELD_MAX_LENGTH } from "../constants"

import { US_COUNTRY_ISO_CODE } from "@/constants/locations"
import type { Country, Region } from "@/graphql/codegen/graphql"
import type {
  AS400PreviewFormValues,
  AddAddressQueryProps,
  AddressFields,
  AerosParentFields,
} from "@/screens/Companies/types/dialogProps"

export function mapAerosFieldToAs400(
  aerosValues: AddressFields,
  { regionList, countryList }: { regionList: Region[]; countryList: Country[] },
  fieldName: AerosParentFields,
  fieldValue: string | null
): Record<AerosParentFields, string> {
  const { city, countryIsoCode, regionIsoCode } = aerosValues

  const isUnitedStates = countryIsoCode === US_COUNTRY_ISO_CODE

  const country = countryList.find(({ isoCode }) => isoCode === countryIsoCode)?.name || ""
  const region = regionList.find(({ isoCode }) => isoCode === regionIsoCode)

  const fieldMapperMap = new Map<AerosParentFields, ((value: string) => string) | (() => string)>([
    ["name", (value) => value?.slice(0, AS400_FIELD_MAX_LENGTH.name)],
    ["address1", (value) => value?.slice(0, AS400_FIELD_MAX_LENGTH.address1)],
    [
      "address2",
      (value) =>
        isUnitedStates
          ? value?.slice(0, AS400_FIELD_MAX_LENGTH.address2)
          : [value, city].filter(Boolean).join(" ").slice(0, AS400_FIELD_MAX_LENGTH.address2),
    ],
    [
      "city",
      (value) =>
        isUnitedStates
          ? value?.slice(0, AS400_FIELD_MAX_LENGTH.city)
          : [region?.name, country].filter(Boolean).join(" ").slice(0, AS400_FIELD_MAX_LENGTH.city),
    ],
    ["postalCode", (value) => value?.slice(0, AS400_FIELD_MAX_LENGTH.postalCode)],
    [
      "regionIsoCode",
      () => (isUnitedStates ? (region?.shortIsoCode ?? "").slice(0, AS400_FIELD_MAX_LENGTH.regionIsoCode) : ""),
    ],
  ])

  const mapper = fieldMapperMap.get(fieldName) || ((value: string) => value)

  return {
    [fieldName]: aerosValues[fieldName] !== undefined ? mapper(fieldValue as string) : "",
  } as Record<AerosParentFields, string>
}

export const updateAS400Field = ({
  key,
  updateParams,
  fieldValue,
}: {
  key: AerosParentFields
  updateParams: {
    methods: UseFormReturn<AddAddressQueryProps>
    formValues: AddressFields
    geoLocations: {
      regionList: Region[]
      countryList: Country[]
    }
  }
  fieldValue: string | null
}): void => {
  const { methods, formValues, geoLocations } = updateParams

  const as400PreviewFields = mapAerosFieldToAs400(formValues, geoLocations, key, fieldValue)
  const fieldKey = `as400.${key}` as keyof AS400PreviewFormValues
  methods.clearErrors(fieldKey)
  methods.setValue(fieldKey, as400PreviewFields[key])
}
